import React from 'react';
import { PersonalValue } from '../../types';

type PersonalValueCategory = 'openness' | 'self-transcendence' | 'self-enhancement' | 'conservation';

interface ValueMeta {
  order: number;
  category: PersonalValueCategory;
  longTitle: string;
  description: string;
  tip: string;
}

const DEFAULT_PRIMARY_COLOR = '#6AAEA0';

const VALUE_ORDER = [
  'Tolerance',
  'Nature',
  'Equality',
  'Dependability',
  'Caring',
  'Humility',
  'Conformity',
  'Duty',
  'Tradition',
  'Security',
  'Safety',
  'Reputation',
  'Influence',
  'Authority',
  'Achievement',
  'Pleasure',
  'Stimulation',
  'Independence',
  'Creativity',
] as const;

const VALUE_METADATA: Record<string, ValueMeta> = {
  Tolerance: {
    order: 0,
    category: 'self-transcendence',
    longTitle: 'Tolerance',
    description: 'Open to different viewpoints, backgrounds, and lifestyles.',
    tip: 'Tip: Spotlight openness to different perspectives.',
  },
  Nature: {
    order: 1,
    category: 'self-transcendence',
    longTitle: 'Nature',
    description: 'Values preservation of the natural environment and sustainability.',
    tip: 'Tip: Highlight sustainability and conservation wins.',
  },
  Equality: {
    order: 2,
    category: 'self-transcendence',
    longTitle: 'Equality',
    description: 'Believes in fairness, empathy, and equal treatment for all.',
    tip: 'Tip: Elevate empathy-driven initiatives and inclusivity.',
  },
  Dependability: {
    order: 3,
    category: 'self-transcendence',
    longTitle: 'Dependability',
    description: 'Prioritises reliability, consistency, and trustworthiness.',
    tip: 'Tip: Prove the brand is steady and trustworthy.',
  },
  Caring: {
    order: 4,
    category: 'self-transcendence',
    longTitle: 'Caring',
    description: 'Guided by empathy, community, and helping others.',
    tip: 'Tip: Tell stories about community and generosity.',
  },
  Humility: {
    order: 5,
    category: 'self-transcendence',
    longTitle: 'Humility',
    description: 'Favours modesty, authenticity, and avoiding overstatement.',
    tip: 'Tip: Keep tone grounded and customer-first.',
  },
  Conformity: {
    order: 6,
    category: 'conservation',
    longTitle: 'Conformity',
    description: 'Values harmony, social expectations, and restraint.',
    tip: 'Tip: Position messaging around harmony and respect.',
  },
  Duty: {
    order: 7,
    category: 'conservation',
    longTitle: 'Duty',
    description: 'Feels a sense of obligation, responsibility, and follow-through.',
    tip: 'Tip: Frame offers as a dependable partner in responsibilities.',
  },
  Tradition: {
    order: 8,
    category: 'conservation',
    longTitle: 'Maintaining Traditions',
    description:
      'May, or may desire to, emphasize the preservation of traditions and customs, including those within family, culture, and religion. May be inclined to do things in a way that maintains continuity with the way they have always been done and may be more altruistic and sympathetic.',
    tip: 'Tip: Consider using themes of family, religion, appreciation for culture and traditional practices and beliefs in your creative and messaging.',
  },
  Security: {
    order: 9,
    category: 'conservation',
    longTitle: 'Security',
    description: 'Seeks predictability, comfort, and reliable surroundings.',
    tip: 'Tip: Promote reliability and stability.',
  },
  Safety: {
    order: 10,
    category: 'conservation',
    longTitle: 'Safety',
    description: 'Focuses on protection, security, and stability in life.',
    tip: 'Tip: Reassure with protection and safeguards.',
  },
  Reputation: {
    order: 11,
    category: 'self-enhancement',
    longTitle: 'Reputation',
    description: 'Cares about public image, status, and being seen positively.',
    tip: 'Tip: Underscore community impact and responsible behaviour.',
  },
  Influence: {
    order: 12,
    category: 'self-enhancement',
    longTitle: 'Acquiring Wealth and Influence',
    description:
      'May be, or may desire to be, more likely to accumulate wealth, and the status and power that comes from money and material possessions. May also be more outgoing, self-disciplined, and organized; and less softhearted. Life is about money and social status.',
    tip: 'Tip: Consider using themes of prestige, importance, prosperity and power attained by accumulating wealth in your creative and messaging.',
  },
  Authority: {
    order: 13,
    category: 'self-enhancement',
    longTitle: 'Authority',
    description: 'Responds to leadership, influence, and authoritative guidance.',
    tip: 'Tip: Feature confident leadership and expertise.',
  },
  Achievement: {
    order: 14,
    category: 'self-enhancement',
    longTitle: 'Show Abilities and Be Admired',
    description:
      'May, or may desire to, seek success and the admiration of others for being successful. Ambitiousness and determination may be distinguishing attributes. Life is about getting ahead, "winning," and impressing others.',
    tip: 'Tip: Consider using themes of being admired, appreciated, successful, and acknowledged in your creative and messaging.',
  },
  Pleasure: {
    order: 15,
    category: 'openness',
    longTitle: 'Pleasure',
    description: 'Motivated by enjoyment, gratification, and positive experiences.',
    tip: 'Tip: Lean into enjoyment and positive feelings.',
  },
  Stimulation: {
    order: 16,
    category: 'openness',
    longTitle: 'Stimulation',
    description: 'Seeks excitement, novelty, and adventurous experiences.',
    tip: 'Tip: Offer energetic experiences and novelty.',
  },
  Independence: {
    order: 17,
    category: 'openness',
    longTitle: 'Independence',
    description: 'Prefers self-direction and personal control of outcomes.',
    tip: 'Tip: Emphasize freedom of choice and personal control.',
  },
  Creativity: {
    order: 18,
    category: 'openness',
    longTitle: 'Creativity',
    description: 'Thrives on individuality, imagination, and fresh ideas.',
    tip: 'Tip: Showcase originality, design thinking, and clever problem solving.',
  },
};

interface NormalizedValue {
  name: string;
  value: number;
  category: PersonalValueCategory;
  meta: ValueMeta;
}

interface ChartGeometry {
  size: number;
  center: number;
  radius: number;
  polygonPath: string;
  rings: number[];
  dashedRadius: number;
  axes: Array<{ x: number; y: number; highlight: boolean }>;
  points: Array<{
    value: NormalizedValue;
    pointX: number;
    pointY: number;
    circleX: number;
    circleY: number;
    labelX: number;
    labelY: number;
    textAnchor: 'start' | 'middle' | 'end';
    baseline: 'baseline' | 'middle' | 'hanging';
    angle: number;
  }>;
  wedges: Array<{ path: string; key: string }>;
  maxValue: number;
  arcLabels: Array<{ id: string; text: string; path: string }>;
}

interface PersonalValuesChartProps {
  values: PersonalValue[];
  primaryColor?: string | null;
  topWriteUps?: string[];
}

export default function PersonalValuesChart({ values, primaryColor, topWriteUps }: PersonalValuesChartProps) {
  const baseColor = normalizeHex(primaryColor) ?? DEFAULT_PRIMARY_COLOR;
  const normalized = React.useMemo(() => normalizeValues(values), [values]);
  const topThree = React.useMemo(
    () =>
      [...normalized]
        .filter((entry) => entry.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 3),
    [normalized],
  );
  const highlightSet = React.useMemo(
    () => new Set(topThree.map((entry) => entry.name)),
    [topThree],
  );
  const hasChartData = React.useMemo(
    () => normalized.some((entry) => entry.value > 0),
    [normalized],
  );
  const geometry = React.useMemo(
    () => buildGeometry(normalized, highlightSet),
    [normalized, highlightSet],
  );

  if (!hasChartData) {
    return null;
  }

  const wedgeColor = mixColor(baseColor, '#0f172a', 0.25);
  const polygonFill = withAlpha(baseColor, 0.22);
  const polygonStroke = mixColor(baseColor, '#0f172a', 0.18);
  const ringColor = mixColor(baseColor, '#ffffff', 0.7);
  const axisColor = mixColor(baseColor, '#94a3b8', 0.5);
  const bubbleColor = mixColor(baseColor, '#ffffff', 0.55);
  const bubbleHighlightColor = mixColor(baseColor, '#0f172a', 0.2);
  const labelColor = '#0f172a';
  const subLabelColor = '#475569';

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-6 md:p-8 lg:p-10 w-full overflow-hidden">
      <div className="flex flex-col gap-2 text-sm text-slate-500 md:flex-row md:flex-wrap md:items-center md:justify-between max-w-full">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: baseColor }} />
          <span>Baseline with Index of 100: Online Adult Population</span>
        </div>
      </div>

      <div className="mt-7 grid gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
        <div className="w-full max-w-full lg:max-w-[520px] lg:pr-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-6">Top 3 Personal Values</h3>
          <div className="space-y-6 text-slate-700 text-sm leading-relaxed break-words">
            {topThree.map((entry, idx) => (
              <div key={entry.name}>
                {idx > 0 ? <div className="border-t border-slate-200 mb-6" /> : null}
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-slate-900 tracking-tight">
                    {`${entry.value} - ${entry.name}`}
                  </h4>
                  <p>{topWriteUps?.[idx]?.trim() || ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative overflow-visible hidden lg:block">
          <svg
            width={geometry.size}
            height={geometry.size}
            viewBox={`0 0 ${geometry.size} ${geometry.size}`}
            className="mx-auto"
          >
            <defs>
              <radialGradient id="pv-background" cx="50%" cy="50%" r="65%">
                <stop offset="0%" stopColor={withAlpha(baseColor, 0.08)} />
                <stop offset="75%" stopColor={withAlpha(baseColor, 0.02)} />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
              {geometry.arcLabels.map((arc) => (
                <path key={arc.id} id={arc.id} d={arc.path} fill="none" />
              ))}
            </defs>

            <circle
              cx={geometry.center}
              cy={geometry.center}
              r={geometry.radius}
              fill="url(#pv-background)"
              stroke={withAlpha(baseColor, 0.12)}
              strokeWidth={1.5}
            />

            {geometry.rings.map((ring) => (
              <circle
                key={`ring-${ring}`}
                cx={geometry.center}
                cy={geometry.center}
                r={ring}
                fill="none"
                stroke={ringColor}
                strokeWidth={1}
              />
            ))}

            <circle
              cx={geometry.center}
              cy={geometry.center}
              r={geometry.dashedRadius}
              fill="none"
              stroke={ringColor}
              strokeWidth={1}
              strokeDasharray="6 6"
            />

            {geometry.arcLabels.map((arc) => (
              <text
                key={`label-${arc.id}`}
                fontSize={12}
                fontWeight={600}
                letterSpacing="0.35em"
                fill="#64748b"
                textAnchor="middle"
              >
                <textPath href={`#${arc.id}`} startOffset="50%">
                  {arc.text}
                </textPath>
              </text>
            ))}

            {geometry.axes.map((axis, idx) => (
              <line
                key={`axis-${idx}`}
                x1={geometry.center}
                y1={geometry.center}
                x2={axis.x}
                y2={axis.y}
                stroke={axis.highlight ? wedgeColor : axisColor}
                strokeWidth={axis.highlight ? 1.6 : 1}
              />
            ))}

            {geometry.wedges.map((wedge) => (
              <path key={wedge.key} d={wedge.path} fill={wedgeColor} opacity={0.9} />
            ))}

            <path
              d={geometry.polygonPath}
              fill={polygonFill}
              stroke={polygonStroke}
              strokeWidth={2}
            />

            {geometry.points.map((point) => {
              const isHighlight = highlightSet.has(point.value.name);
              const displayValue = point.value.value > 0 ? point.value.value.toString() : '–';
              return (
                <g key={point.value.name}>
                  <circle
                    cx={point.circleX}
                    cy={point.circleY}
                    r={18}
                    fill={isHighlight ? bubbleHighlightColor : bubbleColor}
                    stroke={isHighlight ? wedgeColor : withAlpha(baseColor, 0.25)}
                    strokeWidth={isHighlight ? 2 : 1}
                  />
                  <text
                    x={point.circleX}
                    y={point.circleY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: isHighlight ? '12px' : '11px',
                      fontWeight: 600,
                      fill: isHighlight ? '#ffffff' : labelColor,
                    }}
                  >
                    {displayValue}
                  </text>
                  <text
                    x={point.labelX}
                    y={point.labelY}
                    textAnchor={point.textAnchor}
                    dominantBaseline={point.baseline}
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      fill: labelColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    {point.value.name}
                  </text>
                </g>
              );
            })}

            <circle
              cx={geometry.center}
              cy={geometry.center}
              r={32}
              fill="#ffffff"
              stroke={withAlpha(baseColor, 0.2)}
              strokeWidth={1}
            />
            <text
              x={geometry.center}
              y={geometry.center - 6}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: '11px', fontWeight: 500, fill: subLabelColor, letterSpacing: '0.12em' }}
            >
              TOP
            </text>
            <text
              x={geometry.center}
              y={geometry.center + 8}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: '11px', fontWeight: 700, fill: labelColor, letterSpacing: '0.12em' }}
            >
              VALUES
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
}

function normalizeValues(values: PersonalValue[]): NormalizedValue[] {
  type ValueDetails = {
    value: number;
    longTitle?: string;
    description?: string;
    tip?: string;
    category?: PersonalValueCategory;
  };
  const lookup = new Map<string, ValueDetails>();

  values?.forEach((entry) => {
    if (!entry) return;
    const key = (entry.name || '').trim().toLowerCase();
    if (!key) return;
    const numeric = sanitizeNumber(entry.value);
    const longTitle =
      typeof entry.longTitle === 'string' ? entry.longTitle.trim() : undefined;
    const description =
      typeof entry.description === 'string' ? entry.description.trim() : undefined;
    const tip = typeof entry.tip === 'string' ? entry.tip.trim() : undefined;
    const existing = lookup.get(key);
    if (!existing || numeric > existing.value) {
      lookup.set(key, {
        value: numeric,
        longTitle: longTitle ?? existing?.longTitle,
        description: description ?? existing?.description,
        tip: tip ?? existing?.tip,
        category: entry.category ?? existing?.category,
      });
    } else if (existing) {
      if (!existing.longTitle && longTitle) existing.longTitle = longTitle;
      if (!existing.description && description) existing.description = description;
      if (!existing.tip && tip) existing.tip = tip;
      if (!existing.category && entry.category) existing.category = entry.category;
    }
  });

  return VALUE_ORDER.map((label) => {
    const meta = VALUE_METADATA[label] ?? {
      order: 0,
      category: 'openness' as PersonalValueCategory,
      longTitle: label,
      description: 'Core value guiding decision-making and preferences.',
      tip: 'Tip: Connect messaging to real-world expressions of this value.',
    };
    const key = label.trim().toLowerCase();
    const details = lookup.get(key);
    if (details?.longTitle) meta.longTitle = details.longTitle;
    if (details?.description) meta.description = details.description;
    if (details?.tip) meta.tip = details.tip;
    if (details?.category) meta.category = details.category;
    const numeric = details?.value ?? 0;
    return {
      name: label,
      value: numeric,
      category: meta.category,
      meta,
    };
  });
}

function buildGeometry(values: NormalizedValue[], highlightSet: Set<string>): ChartGeometry {
  const radius = 170;
  const labelOffset = 76;
  const circleOffset = 38;
  const margin = 140;
  const size = (radius + labelOffset + margin) * 2;
  const center = size / 2;
  const angleStep = (Math.PI * 2) / values.length;
  const baseAngle = -Math.PI / 2;
  const maxValue = Math.max(180, ...values.map((entry) => entry.value));
  const circleRadius = radius + circleOffset;
  const labelRadius = radius + labelOffset;
  const arcRadius = radius + labelOffset + 102;

  const points = values.map((entry, index) => {
    const angle = baseAngle + index * angleStep;
    const scale = entry.value <= 0 ? 0 : Math.min(entry.value / maxValue, 1);
    const pointRadius = radius * scale;
    const axisX = center + radius * Math.cos(angle);
    const axisY = center + radius * Math.sin(angle);
    const pointX = center + pointRadius * Math.cos(angle);
    const pointY = center + pointRadius * Math.sin(angle);
    const circleX = center + circleRadius * Math.cos(angle);
    const circleY = center + circleRadius * Math.sin(angle);
    const labelX = center + labelRadius * Math.cos(angle);
    const labelY = center + labelRadius * Math.sin(angle);
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const textAnchor: 'start' | 'middle' | 'end' =
      Math.abs(cosine) < 0.25 ? 'middle' : cosine > 0 ? 'start' : 'end';
    const baseline: 'baseline' | 'middle' | 'hanging' =
      Math.abs(sine) < 0.25 ? 'middle' : sine > 0 ? 'hanging' : 'baseline';

    return {
      value: entry,
      pointX,
      pointY,
      circleX,
      circleY,
      labelX,
      labelY,
      textAnchor,
      baseline,
      angle,
      axisX,
      axisY,
    };
  });

  const polygonPath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.pointX.toFixed(2)} ${point.pointY.toFixed(2)}`)
    .join(' ')
    .concat(' Z');

  const rings = Array.from({ length: 4 }, (_, index) => radius * ((index + 1) / 4));
  const dashedRadius = radius * 0.52;

  const axes = points.map((point) => ({
    x: point.axisX,
    y: point.axisY,
    highlight: highlightSet.has(point.value.name),
  }));

  const wedges: Array<{ path: string; key: string }> = [];
  const wedgeInner = radius * 0.18;

  points.forEach((point, index) => {
    if (!highlightSet.has(point.value.name)) return;

    const startAngle = baseAngle + index * angleStep - angleStep * 0.48;
    const endAngle = baseAngle + index * angleStep + angleStep * 0.48;
    const outerRadius = Math.max(radius * 0.68, radius * Math.min(point.value.value / maxValue, 1));
    const tipRadius = outerRadius + 16;

    const innerStart = polarToCartesian(center, center, wedgeInner, startAngle);
    const outerStart = polarToCartesian(center, center, outerRadius, startAngle);
    const tip = polarToCartesian(center, center, tipRadius, point.angle);
    const outerEnd = polarToCartesian(center, center, outerRadius, endAngle);
    const innerEnd = polarToCartesian(center, center, wedgeInner, endAngle);

    const path = [
      `M ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
      `L ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
      `L ${tip.x.toFixed(2)} ${tip.y.toFixed(2)}`,
      `L ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
      `L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
      'Z',
    ].join(' ');

    wedges.push({ path, key: point.value.name });
  });

  const arcSpan = 80;
  const halfArc = arcSpan / 2;
  const arcConfig: Array<{ id: string; text: string; center: number }> = [
    { id: 'arc-open', text: 'OPENNESS TO CHANGE', center: 210 },
    { id: 'arc-transcend', text: 'SELF-TRANSCENDENCE', center: 330 },
    { id: 'arc-conserve', text: 'CONSERVATION', center: 30 },
    { id: 'arc-enhance', text: 'SELF-ENHANCEMENT', center: 150 },
  ];

  const arcLabels = arcConfig.map((entry) => {
    const startDeg = entry.center - halfArc;
    const endDeg = entry.center + halfArc;
    const startAngle = degToRad(startDeg);
    const endAngle = degToRad(endDeg);
    const path = describeArc(center, center, arcRadius, startAngle, endAngle, true);
    return { id: entry.id, text: entry.text, path };
  });

  return {
    size,
    center,
    radius,
    polygonPath,
    rings,
    dashedRadius,
    axes,
    points,
    wedges,
    maxValue,
    arcLabels,
  };
}

function sanitizeNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(numeric) ? Math.round(numeric) : 0;
  }
  return 0;
}

function normalizeHex(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const hexMatch = /^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.exec(trimmed);
  if (!hexMatch) return null;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const sanitized = hex.replace('#', '');
  if (![3, 6].includes(sanitized.length)) return null;
  const expanded = sanitized.length === 3 ? sanitized.split('').map((ch) => ch + ch).join('') : sanitized;
  const intVal = parseInt(expanded, 16);
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(clamp(Math.round(r), 0, 255))}${toHex(clamp(Math.round(g), 0, 255))}${toHex(
    clamp(Math.round(b), 0, 255),
  )}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function mixColor(colorA: string, colorB: string, amount: number): string {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  if (!a || !b) return colorA;
  const ratio = clamp(amount, 0, 1);
  const r = a.r + (b.r - a.r) * ratio;
  const g = a.g + (b.g - a.g) * ratio;
  const bVal = a.b + (b.b - a.b) * ratio;
  return rgbToHex(r, g, bVal);
}

function withAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamp(alpha, 0, 1)})`;
}

function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function describeArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  clockwise: boolean,
): string {
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  let delta = endAngle - startAngle;
  if (clockwise && delta < 0) delta += Math.PI * 2;
  if (!clockwise && delta > 0) delta -= Math.PI * 2;
  if (!clockwise && delta === 0) delta = Math.PI * 2 * -1;
  if (clockwise && delta === 0) delta = Math.PI * 2;
  const largeArcFlag = Math.abs(delta) > Math.PI ? 1 : 0;
  const sweepFlag = clockwise ? 1 : 0;
  return [
    `M ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
    `A ${radius.toFixed(2)} ${radius.toFixed(2)} 0 ${largeArcFlag} ${sweepFlag} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`,
  ].join(' ');
}

function polarToCartesian(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}
